// Geometric and calculation utilities converted from data_centrifuge/lib.coffee

import { calcGradientColors } from '@aytm/app/stats/lib/render/colors'

export const DEFAULT_QUARK = 500
export const DONE_QUARK = 800
export const FULFILL_QUARK = 100

export const ANIMATION_DELAY = 2500

export const formatPoint = (p, angle) => ({
  x: p.x * Math.cos(angle) - p.y * Math.sin(angle),
  y: p.y * Math.cos(angle) + p.x * Math.sin(angle)
})

export const formatTranslate = (d, angle) => {
  const p = formatPoint(d, angle)
  return `translate(${p.x},${p.y})`
}

export const degreesToRadians = angleInDegrees => (Math.PI * angleInDegrees) / 180

export const radiansToDegrees = angleInRadians => (180 * angleInRadians) / Math.PI

export const polarToCartesian = (centerX, centerY, radius, angleInDegrees) => {
  const angleInRadians = degreesToRadians(angleInDegrees)
  return {
    x: centerX + radius * Math.cos(angleInRadians),
    y: centerY + radius * Math.sin(angleInRadians)
  }
}

export const calcSign = (p, P) => Math.sign(p.y * P.x - p.x * P.y)

export const calcDistance = (p1, p2) =>
  Math.sqrt(Math.pow(Math.abs(p1.x - p2.x), 2) + Math.pow(Math.abs(p1.y - p2.y), 2))

export const calcCircleAngle = (p1, p2, r) => {
  const d = calcDistance(p1, p2)
  if (d - 2 * r > 0) {
    return 180
  } else {
    return radiansToDegrees(2 * Math.asin(d / (2 * r)))
  }
}

export const calcCircleRadius = (p1, p2, a) => calcDistance(p1, p2) / (2 * Math.sin(degreesToRadians(a) / 2))

export const calcCircleCenter = (p1, p2, r) => {
  const d = calcDistance(p1, p2)
  return {
    x: (p1.x + p2.x) / 2 + (Math.sqrt(r * r - (d * d) / 4) * (p1.y - p2.y)) / d,
    y: (p1.y + p2.y) / 2 + (Math.sqrt(r * r - (d * d) / 4) * (p2.x - p1.x)) / d
  }
}

export const calcCoordAngle = p => {
  // atan2 handles the axes (x === 0) that the hand-rolled atan + quadrant fix-ups missed:
  // a due-north node (x=0, y<0) resolved to 90 (south) and flipped its tooltip arrow 180 degrees.
  const angle = radiansToDegrees(Math.atan2(p.y, p.x))
  return (angle + 360) % 360
}

export const calcCirclePosition = (x, y, r) => {
  const k = r / Math.sqrt(x * x + y * y)
  return { x: k * x, y: k * y }
}

export const calcArea = coords => {
  let area = 0
  for (let i = 0; i < coords.length; i++) {
    area += coords[i].x * coords[(i + 1) % coords.length].y
    area -= coords[i].y * coords[(i + 1) % coords.length].x
  }
  return area / 2
}

export const calcCentroid = coords => {
  let x = 0
  let y = 0

  for (let i = 0; i < coords.length; i++) {
    const f = coords[i].x * coords[(i + 1) % coords.length].y - coords[(i + 1) % coords.length].x * coords[i].y
    x += (coords[i].x + coords[(i + 1) % coords.length].x) * f
    y += (coords[i].y + coords[(i + 1) % coords.length].y) * f
  }

  const f = 6 * calcArea(coords)
  // Fall back to the arithmetic mean for degenerate polygons (collinear or
  // coincident vertices give area 0 and the shoelace centroid divides by zero,
  // returning Infinity/NaN). The mean is the geometrically reasonable limit.
  if (Math.abs(f) < 1e-9) {
    const n = coords.length || 1
    let mx = 0
    let my = 0
    for (let i = 0; i < coords.length; i++) {
      mx += coords[i].x
      my += coords[i].y
    }
    return { x: mx / n, y: my / n }
  }
  return { x: x / f, y: y / f }
}

export const calcDistancePoints = (k, b, h, p) => {
  const A = k * k + 1
  const B = 2 * k * (b - p.y) - 2 * p.x
  const C = p.x * p.x + Math.pow(b - p.y, 2) - h * h
  // Clamp a negative discriminant to 0: mathematically the circle / line fail
  // to intersect only from floating-point drift in otherwise constructible
  // configurations, so collapse both candidate points onto the perpendicular
  // foot (-B / 2A). Without this clamp, `Math.sqrt(D)` returns NaN and both
  // candidate coordinates come back `undefined`, which propagates into SVG
  // path attributes as malformed geometry.
  const D = Math.max(0, B * B - 4 * A * C)

  const d = Math.sqrt(D)
  let X1, Y1, X2, Y2
  if (d > 0) {
    X1 = (-B + d) / (2 * A)
    Y1 = k * X1 + b
    X2 = (-B - d) / (2 * A)
    Y2 = k * X2 + b
  } else {
    // d === 0 (real double root, or the clamped-to-zero degenerate case)
    X1 = X2 = -B / (2 * A)
    Y1 = Y2 = k * X1 + b
  }

  return [
    { x: X1, y: Y1 },
    { x: X2, y: Y2 }
  ]
}

export const calcHPoints = (points, h, p) => {
  let ps = [
    { x: p.x, y: p.y },
    { x: p.x, y: p.y }
  ]
  if (Math.abs(points[1].y - points[0].y) >= 0.01 && Math.abs(points[1].x - points[0].x) >= 0.001) {
    const k = (points[1].x - points[0].x) / (points[0].y - points[1].y)
    const b = p.y - k * p.x
    ps = calcDistancePoints(k, b, h, p)
  } else {
    let X1 = p.x,
      X2 = p.x,
      Y1 = p.y,
      Y2 = p.y
    if (Math.abs(points[1].y - points[0].y) < 0.01) {
      Y1 = points[1].y + h
      Y2 = points[1].y - h
    }
    if (Math.abs(points[1].x - points[0].x) < 0.001) {
      X1 = points[1].x + h
      X2 = points[1].x - h
    }
    ps = [
      { x: X1, y: Y1 },
      { x: X2, y: Y2 }
    ]
  }
  return ps
}

export const calcMiddlePoint = (points, curveAngle) => {
  const p = { x: (points[0].x + points[1].x) / 2, y: (points[0].y + points[1].y) / 2 }

  const a = degreesToRadians(curveAngle)
  const l = calcDistance(points[0], points[1])
  const h = (l * Math.tan(a / 4)) / 2

  if (h > 0) {
    const ps = calcHPoints(points, h, p)

    const S1 = calcSign(p, ps[0])
    const S2 = calcSign(p, ps[1])

    if (S1 >= 0) {
      return ps[0]
    }
    if (S2 >= 0) {
      return ps[1]
    }
  }

  return p
}

export const calcArcs = (id, s, vs, p, r) => {
  if (vs.length > 1) {
    return vs.map((v, i) => {
      const pV = calcCirclePosition(v.x - p.x, v.y - p.y, r)
      const angle = calcCoordAngle(pV)

      const vS = vs[(i + vs.length - 1) % vs.length]
      const pS = calcCirclePosition(vS.x - p.x, vS.y - p.y, r)

      let angleS = calcCircleAngle(pV, pS, r)
      if (![0, 180, 360].includes(angleS)) {
        const signS = calcSign(pV, pS)
        if (signS < 0) angleS = 360 - angleS
        if (signS == 0) angleS = 180
      }

      const vE = vs[(i + 1) % vs.length]
      const pE = calcCirclePosition(vE.x - p.x, vE.y - p.y, r)

      let angleE = calcCircleAngle(pV, pE, r)
      if (![0, 180, 360].includes(angleE)) {
        const signE = calcSign(pV, pE)
        if (signE > 0) angleE = 360 - angleE
        if (signE == 0) angleE = 180
      }

      let start =
        angle -
        (Math.min(1, Math.max(0, s[v.i])) * angleS) /
          (Math.min(1, Math.max(0, s[v.i])) + Math.min(1, Math.max(0, s[vS.i])))
      let end =
        angle +
        (Math.min(1, Math.max(0, s[v.i])) * angleE) /
          (Math.min(1, Math.max(0, s[v.i])) + Math.min(1, Math.max(0, s[vE.i])))

      start = start < 0 ? 360 - (Math.abs(start) % 360) : start % 360
      end = end < 0 ? 360 - (Math.abs(end) % 360) : end % 360

      start = start > end ? start - 360 : start

      const colors = r > 2 ? [vS.color, v.color, vE.color] : ['#999999']

      return {
        id: `arc-${id}-${v.id}`,
        start,
        middle: angle,
        end,
        r,
        colors,
        score: id,
        vector: v.id
      }
    })
  } else {
    const v = vs[0]
    return [{ id: `arc-${id}-${v.id}`, start: 0, middle: 180, end: 360, r, colors: [v.color], score: id, vector: v.id }]
  }
}

export const calcCoef = (l, L, maxD, minD) => {
  if (L != 2) {
    const k = (2 * minD - maxD) / (maxD * (L - 2))
    const b = (maxD - minD) / maxD - 2 * k
    return k * l + b
  } else {
    return 0.5
  }
}

export const calcGradientArcs = (start, end, r, colorStart, colorEnd, position) => {
  const arcs = []

  // eslint-disable-next-line no-param-reassign
  start = start < 0 ? 360 - (Math.abs(start) % 360) : start % 360
  // eslint-disable-next-line no-param-reassign
  end = end < 0 ? 360 - (Math.abs(end) % 360) : end % 360

  const angle = end >= start ? end - start : 360 - (start - end)

  const a = 0.3 * angle
  const l = 20

  let colors = calcGradientColors(colorStart, colorEnd, 2 * l + 1)
  colors.splice(position == 'start' ? 0 : l + 1, l)

  for (let i = 0; i <= l; i++) {
    let s, e
    if (position == 'start') {
      s = i == l ? start + a : start + (i * a) / l
      e = i == l ? end : s + 2 * Math.max(1, a / l)
    }
    if (position == 'end') {
      s = i == 0 ? start : end - a + ((i - 1) * a) / l
      e = i == 0 ? end - a : Math.min(end, s + 2 * Math.max(1, a / l))
    }

    s = s < 0 ? 360 - (Math.abs(s) % 360) : s % 360
    e = e < 0 ? 360 - (Math.abs(e) % 360) : e % 360

    arcs.push({
      start: s > e ? s - 360 : s,
      end: e,
      r,
      color: colors[i]
    })
  }

  return arcs
}
